import { Injectable } from '@angular/core';
import {
  BookingCalculationService,
  IBookingCalculationData,
  ICalculationOptions,
  roundToTwo
} from '@roller/booking-calculation-service';
import { IProduct } from '@core/product/models/product.model';
import { IProductInfo } from '@core/product/models/product-info.model';
import { IDialogFoodBeverageForm } from '@app/products/product/product-food-beverage/dialog-food-beverage-form.model';
import { hexGenerate } from '@roller/rdl/utilities/string';
import { IModifierGroup, IProductModifier } from '@core/product/models/product-modifiers.models';
import { IVenueConfig } from '@core/venue-config/models/venue-config.model';
import { TProductType } from '@core/product/models/product-type.type';
import { IOrderItem, IOrderItemModifier } from '@core/order/models/order-item.model';

@Injectable({
  providedIn: 'root'
})
export class OrderFoodBeverageService {
  constructor(private calculationService: BookingCalculationService) {}

  /**
   * Calculates the food and beverage cost of items currently in the dialog.
   * Note that this does not include deposits, discounts, transaction or credit card fees.
   */
  getItemDisplayCost(form: IDialogFoodBeverageForm, config: IVenueConfig, parentProduct: IProduct): number {
    if (!form.option) {
      return 0;
    }

    const data: IBookingCalculationData = {
      items: [this.createOrderItem(form, parentProduct)],
      discountResult: [],
      giftCardResult: [],
      costSettings: config.costSettings
    };
    const options: ICalculationOptions = {
      considerDeposit: false,
      guestPayFees: config.paymentPage.guestPayFees
    };

    const calcCost = this.calculationService.getAmounts(data, options);

    return calcCost.subTotal;
  }

  /** Calc the modifier percentage after the option has been set/changed. - set it as the product amount which we use*/
  calcModifierPercentages(groups: IModifierGroup[], cost: number | undefined): IModifierGroup[] {
    groups.forEach((mGroup: IModifierGroup) => {
      mGroup.modifiers = mGroup.modifiers.map((pModifier: IProductModifier) => {
        if (pModifier.percentage) {
          pModifier.amount = roundToTwo(((cost || 0) * pModifier.percentage) / 100);
        }

        return pModifier;
      });
    });
    return groups;
  }

  /** The orderItem for F&B is somewhat unique from the normal checkout with some reduced complexity. */
  createOrderItem(form: IDialogFoodBeverageForm, parentProduct: IProduct, relatedOrderItemKey?: string): IOrderItem {
    const product = form.option as IProductInfo;
    const quantity = form.quantity;
    const formModifiers: IProductModifier[] = Object.keys(form.modifiers)
      .flatMap((key: string) => form.modifiers[key])
      .filter((item): item is IProductModifier => item !== null);
    const modifiers = formModifiers.map((m) => this.getOrderItemModifier(m));
    const additionalGuests = 0;

    return {
      uniqueId: form.uniqueId,
      productId: product.id,
      parentProductId: parentProduct.id,
      parentName: parentProduct.name,
      parentMinPurchase: parentProduct.minPurchase,
      discount: 0,
      addonList: [],
      quantity,
      bookingDate: new Date(),
      groupSize: product.groupSize,
      product,
      cost: product.cost,
      tax: product.tax,
      productType: parentProduct.type as TProductType,
      modifiers,
      formIds: parentProduct.formIds,
      depositAmount: parentProduct.depositAmount,
      costPercentageRequired: parentProduct.depositPercentage || 0,
      perProductBookingFee: product.perProductBookingFee,
      availabilities: parentProduct.availabilities,
      captureTicketHolderName: parentProduct.captureTicketHolderName,
      orderSelections: [],
      optionSetSelections: [],
      hideSessionDuration: parentProduct.hideSessionDuration,
      comments: form.specialInstructions || '',
      relatedOrderItemKey: relatedOrderItemKey || hexGenerate(15),
      additionalGuests: additionalGuests,
      ticketQuantity: quantity * product.groupSize + additionalGuests,
      isUserDefinedGroupSize: false,
      lastUpdatedDate: new Date()
    };
  }

  /**
   * We calculate the percentage on the fly via calcModifierPercentages, meaning we set `amount` AS the percentage based on the option cost
   * When we convert it the modifier to an order item, it can always be false as we amount as the correct $ value
   */
  private getOrderItemModifier(modifier: IProductModifier): IOrderItemModifier {
    return {
      id: modifier.id,
      name: modifier.name,
      amount: modifier.amount || 0,
      quantity: modifier.quantity || 0,
      isPercentage: false
    };
  }
}
